import { useCallback, useEffect, useRef } from 'react';
import { useSetRecoilState } from 'recoil';
import { TaskView } from '~/backend/connector-next-api/connector-next-schemas';
import { Job, jobQueueExpandedState } from '~/state/atoms/jobAtoms';
import { useJobManagement } from '../useJobManagement';
import {
    ConnectorDownloadedResults,
    downloadConnectorResults,
    jobStatusFromTaskState,
    pollTaskStatus,
} from './utils';

/**
 * A hook for orchestrating connector tasks.
 * It processes tasks in the queue, handling upload, polling, and downloading results.
 * @returns {void}
 */
export function useConnectorJobOrchestrator() {
    const { jobQueue, setJobStatus, updateJob } = useJobManagement();
    const expandJobQueue = useSetRecoilState(jobQueueExpandedState);
    const currentlyProcessingJobIdsRef = useRef<Set<string>>(new Set());

    const finalizeDownload = useCallback(
        (
            jobId: string,
            results: ConnectorDownloadedResults,
            pollData: TaskView
        ) => {
            if (results.downloadErrors.length > 0) {
                updateJob(jobId, {
                    status: 'Error',
                    error: results.downloadErrors.join('; '),
                    downloadRetryData: { type: 'connector', pollData },
                });
            } else {
                updateJob(jobId, {
                    status: 'Done',
                    results: results.files,
                    downloadRetryData: undefined,
                });
            }
        },
        [updateJob]
    );

    const processJob = useCallback(
        async (jobToProcess: Job) => {
            const jobId = jobToProcess.id;
            try {
                const pollData = await pollTaskStatus(
                    jobToProcess,
                    setJobStatus
                );
                const status = jobStatusFromTaskState(pollData.state);

                if (status === 'Done') {
                    setJobStatus(jobId, 'Downloading');
                    const results = await downloadConnectorResults(pollData);
                    finalizeDownload(jobId, results, pollData);
                } else if (status === 'Error') {
                    // failureReason is a client-safe category by contract; the
                    // PHI-bearing detail stays in the connector's own logs.
                    setJobStatus(
                        jobId,
                        'Error',
                        pollData.failureReason ?? 'Task failed on backend'
                    );
                } else if (status === 'Canceled') {
                    setJobStatus(jobId, 'Error', 'Task canceled');
                }
            } catch (error) {
                console.error(
                    `Orchestrator: Error during upload phase for job ${jobId}:`,
                    error
                );
                if (typeof error === 'string') {
                    setJobStatus(jobId, 'Error', error);
                } else if (error instanceof Error) {
                    setJobStatus(jobId, 'Error', error.message);
                } else {
                    setJobStatus(jobId, 'Error', 'Error processing task.');
                }
            }
        },
        [setJobStatus, finalizeDownload]
    );

    const retryDownload = useCallback(
        async (jobToRetry: Job) => {
            const { id: jobId, downloadRetryData } = jobToRetry;
            if (!downloadRetryData || downloadRetryData.type !== 'connector') {
                setJobStatus(jobId, 'Error', 'Cannot retry: missing data.');
                return;
            }

            try {
                setJobStatus(jobId, 'Downloading');
                const results = await downloadConnectorResults(
                    downloadRetryData.pollData
                );
                finalizeDownload(jobId, results, downloadRetryData.pollData);
            } catch (error) {
                if (error instanceof Error) {
                    setJobStatus(jobId, 'Error', error.message);
                } else {
                    setJobStatus(jobId, 'Error', 'Retry download failed.');
                }
            }
        },
        [setJobStatus, finalizeDownload]
    );

    useEffect(() => {
        const jobsToProcess = jobQueue.jobs.filter(
            (job) =>
                (job.status === 'Queued' || job.status === 'RetryDownload') &&
                !currentlyProcessingJobIdsRef.current.has(job.id)
        );
        if (jobsToProcess.length > 0) {
            expandJobQueue(true);
        }
        jobsToProcess.forEach((job) => {
            currentlyProcessingJobIdsRef.current.add(job.id);
            if (job.status === 'RetryDownload') {
                retryDownload(job).finally(() => {
                    currentlyProcessingJobIdsRef.current.delete(job.id);
                });
            } else {
                processJob(job).finally(() => {
                    currentlyProcessingJobIdsRef.current.delete(job.id);
                });
            }
        });
    }, [
        expandJobQueue,
        jobQueue.jobs,
        processJob,
        retryDownload,
        setJobStatus,
    ]);
}
